// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTableSortable.tsx
 * @input React, types, Icon, theme tokens, i18n (useTranslator)
 * @output Exports useTableSortable hook and sort-related types
 * @position Sortable plugin; consumed by Table via plugins prop
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Table/Table.doc.mjs (sort documentation)
 * - /packages/core/src/Table/index.ts (exports)
 */
import { useRef, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../../../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars } from "../../../theme/tokens.stylex.js";
import { Icon } from "../../../Icon/index.js";
import { resolveContextActions } from "../../tableContextMenu.js";
import { useTranslator } from "../../../i18n/index.js";

// =============================================================================
// Sort Types
// =============================================================================

/**
 * Sort direction for a single column.
 */

/**
 * A single sort entry in the sort state array.
 */

/**
 * Complete sort state — an ordered array of sort entries.
 * The first entry is the primary sort; subsequent entries are tiebreakers.
 * Empty array = no sort applied.
 *
 * @example
 * ```
 * const sort: TableSortState = [
 *   { sortKey: 'name', direction: 'ascending' },
 *   { sortKey: 'age', direction: 'descending' },
 * ];
 * ```
 */

// =============================================================================
// Hook Config
// =============================================================================

/**
 * Configuration for useTableSortable.
 *
 * Follows Astryx headless plugin conventions: the consumer owns all state
 * and provides callbacks. The plugin never holds internal sort state.
 *
 * @template TSortKey - Union of valid sort key strings
 *
 * @example
 * ```
 * const [sort, setSort] = useState<TableSortState>([
 *   { sortKey: 'name', direction: 'ascending' },
 * ]);
 * const sortPlugin = useTableSortable({ sort, onSortChange: setSort });
 * <Table plugins={{ sort: sortPlugin }} columns={columns} data={data} />
 * ```
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime"; // =============================================================================
// Styles
// =============================================================================
// =============================================================================
// Helpers
// =============================================================================

function resolveSortKey(column) {
  const {
    sortable
  } = column;
  if (!sortable) {
    return null;
  }
  if (sortable === true) {
    return column.key;
  }
  return sortable.sortKey ?? column.key;
}
function getHeaderLabel(column) {
  const {
    header
  } = column;
  if (typeof header === 'string') {
    return header;
  }
  return column.key;
}
function buildAriaLabel(t, column, direction, rank, total) {
  const label = getHeaderLabel(column);
  if (direction == null) {
    return t('@astryx.table.sort.sortBy', {
      label
    });
  }
  const directionLabel = direction === 'ascending' ? t('@astryx.table.sort.direction.ascending') : t('@astryx.table.sort.direction.descending');
  if (rank != null && total > 1) {
    return t('@astryx.table.sort.sortedByWithPriority', {
      label,
      direction: directionLabel,
      rank,
      total
    });
  }
  return t('@astryx.table.sort.sortedBy', {
    label,
    direction: directionLabel
  });
}
function getNextDirection(current, allowUnsortedState) {
  if (current == null) {
    return 'ascending';
  }
  if (current === 'ascending') {
    return 'descending';
  }
  // current === 'descending'
  return allowUnsortedState ? null : 'ascending';
}

// =============================================================================
// Sort Header Button Component
// =============================================================================

function SortHeaderButton({
  column,
  children,
  configRef
}) {
  const t = useTranslator();
  const config = configRef.current;
  const sortKey = resolveSortKey(column) ?? '';
  const entryIndex = config.sort.findIndex(e => e.sortKey === sortKey);
  const entry = entryIndex >= 0 ? config.sort[entryIndex] : null;
  const direction = entry?.direction ?? null;
  const isMultiSort = config.isMultiSortEnabled === true && config.sort.length > 1;
  const rank = isMultiSort && entryIndex >= 0 ? entryIndex + 1 : null;
  const iconName = direction === 'ascending' ? 'arrowUp' : direction === 'descending' ? 'arrowDown' : 'arrowsUpDown';
  const ariaLabel = buildAriaLabel(t, column, direction, rank, config.sort.length);
  const handleClick = e => {
    const cfg = configRef.current;
    const isShift = e.shiftKey && cfg.isMultiSortEnabled;
    const allowUnsorted = cfg.allowUnsortedState ?? true;
    if (isShift) {
      // Multi-sort: toggle in place or append
      const idx = cfg.sort.findIndex(s => s.sortKey === sortKey);
      if (idx >= 0) {
        const nextDir = getNextDirection(cfg.sort[idx].direction, allowUnsorted);
        if (nextDir == null) {
          // Remove from array
          const newSort = [...cfg.sort];
          newSort.splice(idx, 1);
          cfg.onSortChange(newSort);
        } else {
          const newSort = [...cfg.sort];
          newSort[idx] = {
            ...newSort[idx],
            direction: nextDir
          };
          cfg.onSortChange(newSort);
        }
      } else {
        // Append new entry
        cfg.onSortChange([...cfg.sort, {
          sortKey,
          direction: 'ascending'
        }]);
      }
    } else {
      // Single-sort: replace entire array
      const currentEntry = cfg.sort.find(s => s.sortKey === sortKey);
      const nextDir = getNextDirection(currentEntry?.direction ?? null, allowUnsorted);
      if (nextDir == null) {
        cfg.onSortChange([]);
      } else {
        cfg.onSortChange([{
          sortKey,
          direction: nextDir
        }]);
      }
    }
  };
  return /*#__PURE__*/_jsxs("button", {
    type: "button",
    ...{
      className: "x78zum5 x6s0dn4 xzye2dw x1717udv x1ghz6dp x1ypdohk xln7xf2 x1heor9g xh8yej3 x5yr21d x16tdsg8 x1a2a7pz x17nn4n9 x1hl8ikr xx3sua9"
    },
    "aria-label": ariaLabel,
    onClick: handleClick,
    children: [/*#__PURE__*/_jsx("span", {
      children: children
    }), /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x3nfvp2 x3onkmb xof6bs x25t5g8"
        },
        1: {
          className: "x3nfvp2"
        }
      }[!!(direction != null) << 0],
      children: /*#__PURE__*/_jsx(Icon, {
        icon: iconName,
        size: "xsm",
        color: direction != null ? 'accent' : 'secondary'
      })
    }), rank != null && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x1k6wstc xo5v014 xqwr325"
      },
      "aria-hidden": "true",
      children: rank
    })]
  });
}

// =============================================================================
// Hook
// =============================================================================

/**
 * useTableSortable — table plugin for column sorting.
 *
 * Returns a stable TablePlugin<T> that transforms header cells to add
 * clickable sort indicators. Follows the headless pattern: consumer owns
 * sort state, plugin provides UI and interaction.
 *
 * @example
 * ```
 * const [sort, setSort] = useState<TableSortState>([]);
 * const sortPlugin = useTableSortable({ sort, onSortChange: setSort });
 * <Table
 *   data={users}
 *   columns={[
 *     { key: 'name', header: 'Name', sortable: true },
 *     { key: 'age', header: 'Age', sortable: true },
 *   ]}
 *   plugins={{ sort: sortPlugin }}
 * />
 * ```
 */
export function useTableSortable(config) {
  const t = useTranslator();
  const configRef = useRef(config);
  configRef.current = config;
  return useMemo(() => ({
    transformHeaderCell(props, column) {
      const sortKey = resolveSortKey(column);
      if (sortKey == null) {
        return props;
      }
      const cfg = configRef.current;
      const entry = cfg.sort.find(e => e.sortKey === sortKey);

      // Context-menu actions are built lazily (only when the menu opens) via
      // a getter, reading the current sort state at call time — so we don't
      // build an action array for every header on every render, and the
      // checked/clear state always reflects the latest sort.
      const getSortActions = () => {
        const c = configRef.current;
        const dir = c.sort.find(e => e.sortKey === sortKey)?.direction ?? null;
        const actions = [{
          id: 'sort-asc',
          group: 'sort',
          label: t('@astryx.table.sort.ascending'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "arrowUp",
            size: "xsm",
            "aria-hidden": true
          }),
          checked: dir === 'ascending',
          onSelect: () => c.onSortChange([{
            sortKey: sortKey,
            direction: 'ascending'
          }])
        }, {
          id: 'sort-desc',
          group: 'sort',
          label: t('@astryx.table.sort.descending'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "arrowDown",
            size: "xsm",
            "aria-hidden": true
          }),
          checked: dir === 'descending',
          onSelect: () => c.onSortChange([{
            sortKey: sortKey,
            direction: 'descending'
          }])
        }];
        if (dir != null) {
          actions.push({
            id: 'sort-clear',
            group: 'sort-clear',
            label: t('@astryx.table.sort.clear'),
            icon: /*#__PURE__*/_jsx(Icon, {
              icon: "close",
              size: "xsm",
              "aria-hidden": true
            }),
            onSelect: () => c.onSortChange(c.sort.filter(e => e.sortKey !== sortKey))
          });
        }
        return actions;
      };

      // Merge with any actions a prior plugin contributed (array or getter),
      // resolving both lazily at open time.
      const priorActions = props.contextMenuActions;
      return {
        ...props,
        htmlProps: {
          ...props.htmlProps,
          ...(entry != null ? {
            'aria-sort': entry.direction
          } : {})
        },
        content: /*#__PURE__*/_jsx(SortHeaderButton, {
          column: column,
          configRef: configRef,
          children: props.content
        }),
        contextMenuActions: () => [...resolveContextActions(priorActions), ...getSortActions()]
      };
    }
  }), [t]);
}